import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IconSize } from '@lux/icons';
import { PlusEarlyAccessBadge } from '@browsing/plus';
import { useConfig } from '@browsing/config';
import { trackSelectPromotion } from '@browsing/tracking';

import { RootState } from '@client/redux/types';
import { trackOpenCampaignClick } from '@client/helpers/tracking';
import { buildCampaignUrl } from '@client/helpers/campaignUrl';
import { VALENTINE_EVENT_DELIVERY_PROMISE } from '@client/config/constants';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice';
import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import { makeGetCampaignCategories } from '@client/redux/modules/myloungeMetadata/selectors';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import * as S from '@client/components/mylounge/campaigns/components/campaign-card/campaign-card.styles';
import { useDeliveryLabel } from '@client/components/mylounge/hooks/use-delivery-label';
import { useVariant } from '@client/lib/assignments';
import { Campaign } from '@shared/types/mylounge';
import { CampaignLabel } from '../campaign-label';
import CampaignOverlay from '../campaign-overlay';

const trackIsCECCampaign = {
    [VALENTINE_EVENT_DELIVERY_PROMISE]: 'valentines',
};

interface Props {
    campaign: Campaign;
    position: string;
    imageUrl: string;
    minHeight?: number;
    isPlusEnabled: boolean;
}

export const CampaignCard: FunctionComponent<Props> = ({
    campaign,
    position,
    isPlusEnabled,
    imageUrl,
    minHeight,
}) => {
    const config = useConfig();

    const categoryTabId = useSelector(getCategoryTabId);
    const tracking = useSelector(getTrackingData);
    const { isSpecialDeliveryBanner } = useDeliveryLabel(campaign);
    const getCampaignSubLineCategory = useMemo(makeGetCampaignCategories, []);
    const sublineCategory = useSelector((state: RootState) =>
        getCampaignSubLineCategory(state, campaign.campaignId),
    );
    const destination = buildCampaignUrl(campaign.campaignId, categoryTabId);
    const isMobileScreen = useSelector(getIsMobileScreen);

    const trackingFunc = useCallback(() => {
        const options = {
            categoryTabId,
            position,
            visitorID: tracking ? tracking.visitorID : null,
        };
        localStorage.setItem('navigationFrom', 'myLounge');
        const component =
            trackIsCECCampaign[campaign?.deliveryPromise?.type as keyof typeof trackIsCECCampaign];

        if (component) {
            // @ts-ignore
            options.component = component;
        }
        trackOpenCampaignClick(campaign, options);

        if (config.isEnabled('ga4')) {
            const index = position?.split('|')[0];

            trackSelectPromotion({
                creativeName: 'open',
                itemListName: 'open',
                item: {
                    campaignIdentifier: campaign.campaignId,
                    categoryId: categoryTabId,
                    index: isMobileScreen ? Number(index) : Number(index) - 1,
                    earlyAccessItem: campaign.plusEarlyAccess ? 1 : 0,
                },
            });
        }
    }, [campaign, categoryTabId, config, isMobileScreen, position, tracking]);

    const campaignId = `open-${campaign.campaignId}`;

    const coverWithoutLogo = !campaign.hasLogo;
    const isEarlyAccess = campaign.plusEarlyAccess;
    const isPlusEarlyAccess = isPlusEnabled && isEarlyAccess;
    const isCampaignTitleExperiment = useVariant(
        '9df589f0-9668-4a1a-a051-cf499ec1baa8',
        'variant_campaignTitle',
    );
    const isHideCgExperiment = useVariant(
        'e5b89011-ae1c-47d3-86b6-ddc5df96fb89',
        'variant_categorieshidden',
    );

    return (
        <S.Wrapper
            id={campaignId}
            onClick={trackingFunc}
            data-position={position}
            $x={campaign.x}
            $y={campaign.y}
            $w={campaign.w}
            $h={campaign.h}
            minHeight={minHeight}
            href={destination}
        >
            <S.ImageWrapper id="image_wrappper" withoutLogo={coverWithoutLogo}>
                <S.CampaignImage src={imageUrl} alt={campaign.name} />
                <CampaignOverlay name={campaign.name} hoverClassName="overlay" />
                {!isCampaignTitleExperiment && coverWithoutLogo && (
                    <S.Title>{campaign.name}</S.Title>
                )}
                {isPlusEarlyAccess && (
                    <S.EarlyAccessWrapper>
                        <PlusEarlyAccessBadge size={IconSize.S} />
                    </S.EarlyAccessWrapper>
                )}
            </S.ImageWrapper>
            <S.InnerWrapper isHideCgExperiment={isHideCgExperiment}>
                <CampaignLabel
                    campaign={campaign}
                    isSpecialDeliveryBanner={isSpecialDeliveryBanner}
                    categorySubline={sublineCategory}
                />
            </S.InnerWrapper>
        </S.Wrapper>
    );
};
